import { resolveApiBase, SportsApiClient, text, toArray, type WidgetProps } from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'

export const DRIVER_STANDINGS_ATTRIBUTES = [
  'sport',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface DriverStandingsProps extends WidgetProps {
  sport?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

// Motorsport championship table (SofaScore/Flashscore look): a rank, the driver
// with their team underneath, and the points bold on the right. Reads the
// `<sport>-drivers` feed (standings.drivers.driver[]).
export const driverStandingsStyles = `
  .table-wrap { overflow-x: auto; }
  table.sports-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  table.sports-table th, table.sports-table td { padding: 0.55rem 0.5rem; }
  table.sports-table thead th {
    font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--sports-muted); font-weight: 600; text-align: right;
    border-bottom: 1px solid var(--sports-border);
  }
  table.sports-table thead th.dh { text-align: left; padding-left: 0.9rem; }
  table.sports-table td { border-bottom: 1px solid var(--sports-border); text-align: right; font-variant-numeric: tabular-nums; }
  table.sports-table tbody tr:hover { background: color-mix(in srgb, var(--sports-fg) 4%, transparent); }
  td.rank { width: 2rem; text-align: center; color: var(--sports-muted); }
  td.pts { font-weight: 700; }
  td.driver { text-align: left; }
  .dcell { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
  .dcell .nm { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dcell .team { color: var(--sports-muted); font-size: 0.76rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .see-more {
    display: block; width: 100%; padding: 0.6rem 1rem; text-align: center;
    background: var(--sports-card); color: var(--sports-muted);
    border: none; border-top: 1px solid var(--sports-border);
    font: inherit; font-size: 0.82rem; cursor: pointer;
  }
  .see-more:hover { color: var(--sports-fg); }
`

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

interface Driver {
  pos: string
  name: string
  team: string
  points: string
}

function parseDrivers(payload: unknown): Driver[] {
  const list = (payload as { standings?: { drivers?: { driver?: unknown } } })?.standings?.drivers?.driver
  return toArray<Record<string, unknown>>(list as Record<string, unknown>[] | undefined).map((d) => ({
    pos: text(d, 'pos', 'position'),
    name: text(d, 'name'),
    team: text(d, 'team'),
    points: text(d, 'points'),
  }))
}

export function DriverStandings({
  sport = 'motogp',
  lang = 'es',
  limit,
  heading,
  refresh,
  apiBase,
}: DriverStandingsProps) {
  const max = number(limit, 30)
  const refreshSeconds = number(refresh, 600)
  const base = resolveApiBase(apiBase)
  const t = useLabels(base, lang)
  const [expanded, setExpanded] = useState(false)

  const { data, error, loading } = useFeed(
    (signal) => new SportsApiClient(base).feed(sport, `${sport}-drivers`, signal),
    [base, sport],
    refreshSeconds,
  )

  const rows = (data ? parseDrivers(data) : []).filter((d) => d.name).slice(0, max)
  // Show the first 10 rows by default; the "Ver más" footer reveals the rest.
  const visible = expanded ? rows : rows.slice(0, 10)

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? t('driverStandings', 'Clasificación de pilotos')}</span>
      </div>

      {loading && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {!loading && error && (
        <div class="sports-note error">{t('tableUnavailable', 'La clasificación no está disponible.')}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div class="sports-note">{t('noStandings', 'Sin clasificación publicada.')}</div>
      )}

      {rows.length > 0 && (
        <div class="table-wrap">
          <table class="sports-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="dh">{t('driver', 'Piloto')}</th>
                <th>{t('points', 'Pts')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d, i) => (
                <tr key={d.name}>
                  <td class="rank">{d.pos || i + 1}</td>
                  <td class="driver">
                    <span class="dcell">
                      <span class="nm">{d.name}</span>
                      {d.team && <span class="team">{d.team}</span>}
                    </span>
                  </td>
                  <td class="pts">{d.points || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 10 && (
        <button class="see-more" type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('seeLess', 'Ver menos') : `${t('seeMore', 'Ver más')} (${rows.length})`}
        </button>
      )}
    </div>
  )
}
